import React from 'react';
import PropTypes from 'prop-types';
import constants from '../../qti/Constants';
import presignUrlService from '../../services/PresignUrlService';
import questionHelperService from '../../services/questionView/QuestionHelperServices';
import styles from './additionalResourcesBar.scss';
import { t } from '../../services/i18nService';
import utilService from '../../services/UtilService';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import { reportError } from '../../services/ErrorReporting';

class AdditionalResource extends React.PureComponent {
    static propTypes = {
        url: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        // Provided by FileAdditionalResource for offline cache lookup (browser-based offline):
        // the URL the PDF was cached under in the service worker, matching the key used by
        // cacheQuestionSetMedia (differs from the online /file/ci/ endpoint).
        cachedServletUrl: PropTypes.string,
        presignedUrl: PropTypes.string,
    };

    // Attempts to open the PDF from CacheStorage when the MPP flag is enabled.
    // Always tried first regardless of online status — navigator.onLine is unreliable
    // when the device is connected to a local network but has no actual internet access.
    // Tries cachedServletUrl first (stable key), then presignedUrl as a fallback.
    // useLocalViewer=true ensures the viewer URL stays within the SW's /player/ scope.
    // Treat any non-null match as a hit: opaque responses (cross-origin no-cors fetches)
    // have ok===false and status===0 but are still servable by the service worker.
    // Returns true if the PDF was opened from cache, false otherwise.
    tryOpenFromCache = async (pdfJsService, cachedServletUrl, presignedUrl) => {
        if (!marketplacePropertiesService.isOfflineResilienceForPdfViewerEnabled()
            || !('caches' in window)) {
            return false;
        }
        try {
            const urlsToTry = [cachedServletUrl, presignedUrl].filter(Boolean);
            for (const cachedUrl of urlsToTry) {
                // eslint-disable-next-line no-await-in-loop
                const cachedResponse = await caches.match(cachedUrl);
                if (cachedResponse) {
                    window.open(pdfJsService.getPdfJsUrl(cachedUrl, true), '_blank');
                    return true;
                }
            }
        } catch (err) {
            reportError(err);
        }
        return false;
    };

    openUrl = async (e) => {
        e.preventDefault();
        const { url, type, cachedServletUrl, presignedUrl } = this.props;
        if (type === constants.additionalResourceType.FILE) {
            const pdfJsService = questionHelperService.getServices().pdfJsService;
            if (await this.tryOpenFromCache(pdfJsService, cachedServletUrl, presignedUrl)) {
                return;
            }
            const presignPromise = presignUrlService.loadPresignedUrl(url, url);
            if (presignPromise && typeof presignPromise.then === 'function') {
                await presignPromise.then((resolvedPresignedUrl) => {
                    window.open(pdfJsService.getPdfJsUrl(resolvedPresignedUrl), '_blank');
                }).catch((err) => {
                    reportError(err);
                });
            }
        } else {
            utilService.openInNewWindow(url);
        }
    };

    render() {
        const { title, type } = this.props;
        let icon;
        let rel;

        switch (type) {
        case constants.additionalResourceType.FILE:
            // If opening file it is PDF and then pass opener to allow error tracking back to player
            icon = 'fa-file';
            rel = 'opener';
            break;

        case constants.additionalResourceType.RUBRIC:
            icon = 'fa-th-large';
            rel = 'noopener noreferrer';
            break;

        default:
            icon = 'fa-globe';
            rel = 'noopener noreferrer';
            break;
        }

        return (
            <li>
                <button
                    role="link"
                    onClick={this.openUrl}
                    className={styles.resourceButton}
                    rel={rel}
                    aria-label={`${title} (${t('general.opensInNewWindow')})`}
                >
                    <i className={`fa ${icon}`} aria-hidden="true" />
                    { title }

                    <i className={`fa fa-external-link ${styles.external}`} aria-hidden="true" />
                </button>
            </li>
        );
    }
}

export default AdditionalResource;
